import matter from 'gray-matter';
import YAML from 'yaml';
import { normalizeAdventure, } from './adventure.js';
import { resolveKind, bodyFieldKey, normalizeKindId } from './kinds.js';
/**
 * Markdown dialect (de)serializer (plan §5).
 *
 * An adventure is stored as a folder of Markdown files:
 *   adventure.md            — manifest (frontmatter: meta, overview, custom `kinds:`, `sections:`)
 *   places/<id>.md          — one file per entity (read-aloud/event/details as `:::` fences)
 *   characters/<id>.md  npcs/<id>.md  beasts/<id>.md  encounters/<id>.md
 *   clues/<id>.md  items/<id>.md  pages/<id>.md
 *   <storageDir>/<id>.md    — custom-kind entities
 *
 * Dialect = YAML frontmatter (structured fields + refs) + Markdown body (prose) + custom
 * fences. The parser reconstructs the SAME in-memory `Adventure` object the runtime consumes,
 * guaranteeing `parseAdventure(serializeAdventure(adv))` deep-equals `normalizeAdventure(adv)`.
 */
export const MANIFEST_FILE = 'adventure.md';
/** Built-in entity kinds that map to a dedicated in-memory bucket. */
const ENTITY_KINDS = [
    { kind: 'place', dir: 'places', body: 'description', get: (a) => a.rooms.rooms, set: (a, v) => { a.rooms.rooms = v; } },
    { kind: 'character', dir: 'characters', body: 'backstory', get: (a) => a.characters, set: (a, v) => { a.characters = v; } },
    { kind: 'npc', dir: 'npcs', body: 'description', get: (a) => a.npcs, set: (a, v) => { a.npcs = v; } },
    { kind: 'beast', dir: 'beasts', body: 'notes', get: (a) => a.monsters, set: (a, v) => { a.monsters = v; } },
    { kind: 'encounter', dir: 'encounters', body: 'notes', get: (a) => a.encounters, set: (a, v) => { a.encounters = v; } },
    { kind: 'clue', dir: 'clues', body: 'text', get: (a) => a.clues, set: (a, v) => { a.clues = v; } },
    { kind: 'item', dir: 'items', body: 'description', get: (a) => a.items, set: (a, v) => { a.items = v; } },
    { kind: 'page', dir: 'pages', body: 'markdown', get: (a) => a.pages, set: (a, v) => { a.pages = v; } },
];
const ENTITY_KIND_BY_DIR = new Map(ENTITY_KINDS.map((e) => [e.dir, e]));
const ENTITY_KIND_BY_KIND = new Map(ENTITY_KINDS.map((e) => [e.kind, e]));
/** Keys moved out of `place` frontmatter into the Markdown body / `links:` block. */
const PLACE_FENCE_KEYS = ['readAloud', 'event', 'details'];
const PLACE_LINK_KEYS = ['npcs', 'items', 'clues', 'connections'];
// ---------------------------------------------------------------------------
// Frontmatter document (de)serialization
// ---------------------------------------------------------------------------
const yamlEngine = {
    parse: (str) => YAML.parse(str) ?? {},
    stringify: (obj) => YAML.stringify(obj),
};
const MATTER_OPTS = { engines: { yaml: yamlEngine }, language: 'yaml' };
function stringifyDoc(data, body) {
    const yamlStr = YAML.stringify(data).replace(/\n+$/, '');
    const trimmed = (body ?? '').trim();
    return `---\n${yamlStr}\n---\n${trimmed ? `\n${trimmed}\n` : ''}`;
}
function parseDoc(content) {
    const parsed = matter(content, MATTER_OPTS);
    return { data: parsed.data ?? {}, body: (parsed.content ?? '').trim() };
}
// ---------------------------------------------------------------------------
// Custom fences
// ---------------------------------------------------------------------------
const FENCE_RE = /:::([a-z][a-z-]*)(?:[ \t]+([^\n]*))?\n([\s\S]*?)\n?:::/g;
function fence(type, content, name) {
    const head = name ? `:::${type} ${name}` : `:::${type}`;
    return `${head}\n${String(content).trim()}\n:::`;
}
/** Pull all `:::type ...` fences out of a body, returning the stripped prose + fences. */
function extractFences(body) {
    const fences = [];
    const prose = body
        .replace(FENCE_RE, (_m, type, name, content) => {
        fences.push({ type, name: name?.trim() || undefined, content: content.trim() });
        return '';
    })
        .replace(/\n{3,}/g, '\n\n')
        .trim();
    return { prose, fences };
}
// ---------------------------------------------------------------------------
// Entity (de)serialization
// ---------------------------------------------------------------------------
function isEmpty(value) {
    if (value == null)
        return true;
    if (typeof value === 'string')
        return value.trim() === '';
    if (Array.isArray(value))
        return value.length === 0;
    return false;
}
/** Serialize one entity to a Markdown document. `place` gets fence + `links:` handling. */
function serializeEntity(entity, kindId, bodyField) {
    const consumed = new Set(['id', 'kind', bodyField]);
    let body = String(entity[bodyField] ?? '').trim();
    let fencesBlock = '';
    const data = { id: entity.id, kind: kindId };
    if (kindId === 'place') {
        for (const k of PLACE_FENCE_KEYS)
            consumed.add(k);
        for (const k of PLACE_LINK_KEYS)
            consumed.add(k);
        const parts = [];
        if (!isEmpty(entity.readAloud))
            parts.push(fence('read-aloud', entity.readAloud));
        if (!isEmpty(entity.event))
            parts.push(fence('event', entity.event));
        for (const d of entity.details ?? []) {
            if (!isEmpty(d))
                parts.push(fence('details', d));
        }
        fencesBlock = parts.join('\n\n');
    }
    for (const [k, v] of Object.entries(entity)) {
        if (consumed.has(k) || v === undefined)
            continue;
        data[k] = v;
    }
    if (kindId === 'place') {
        const links = {};
        for (const k of PLACE_LINK_KEYS) {
            const v = entity[k];
            if (Array.isArray(v) && v.length)
                links[k] = v;
        }
        if (Object.keys(links).length)
            data.links = links;
    }
    const fullBody = [body, fencesBlock].filter(Boolean).join('\n\n');
    return stringifyDoc(data, fullBody);
}
/** Parse one entity document back into its in-memory shape. */
function parseEntity(content, kindId, bodyField) {
    const { data, body } = parseDoc(content);
    const { kind: _kind, links, ...rest } = data;
    void _kind;
    const entity = { ...rest };
    if (kindId === 'place') {
        if (links && typeof links === 'object') {
            for (const [k, v] of Object.entries(links))
                entity[k] = v;
        }
        const { prose, fences } = extractFences(body);
        entity[bodyField] = prose;
        const readAloud = fences.find((fnc) => fnc.type === 'read-aloud');
        const event = fences.find((fnc) => fnc.type === 'event');
        const details = fences.filter((fnc) => fnc.type === 'details').map((fnc) => fnc.content);
        if (readAloud)
            entity.readAloud = readAloud.content;
        if (event)
            entity.event = event.content;
        if (details.length)
            entity.details = details;
    }
    else {
        entity[bodyField] = body;
    }
    return entity;
}
function serializeManifest(adv, entityOrder) {
    const data = {
        schemaVersion: 3,
        slug: adv.slug,
        title: adv.title,
    };
    if (adv.subtitle)
        data.subtitle = adv.subtitle;
    if (adv.cover)
        data.cover = adv.cover;
    if (adv.meta && Object.keys(adv.meta).length)
        data.meta = adv.meta;
    if (adv.overview && Object.keys(adv.overview).length)
        data.overview = adv.overview;
    if (adv.curses)
        data.curses = adv.curses;
    if (adv.authorNotes)
        data.authorNotes = adv.authorNotes;
    if (adv.rooms.floors.length)
        data.floors = adv.rooms.floors;
    if (adv.images && Object.keys(adv.images).length)
        data.images = adv.images;
    if (adv.kinds && Object.keys(adv.kinds).length)
        data.kinds = adv.kinds;
    if (Object.keys(entityOrder).length)
        data.entityOrder = entityOrder;
    data.sections = adv.sections.map((s) => {
        const out = { id: s.id, kind: s.kind ?? normalizeKindId(s.type) };
        out.title = s.title;
        out.icon = s.icon;
        if (s.scope)
            out.scope = s.scope;
        if (s.fixed)
            out.fixed = true;
        if (s.enabled === false)
            out.enabled = false;
        out.order = s.order;
        if (s.pageId)
            out.pageId = s.pageId;
        return out;
    });
    if (adv.createdAt)
        data.createdAt = adv.createdAt;
    if (adv.updatedAt)
        data.updatedAt = adv.updatedAt;
    return stringifyDoc(data, '');
}
// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
/** Reorder a parsed bucket to match the manifest's recorded order (unknowns appended). */
function reorderByIndex(list, order) {
    if (!order || !order.length)
        return list;
    const rank = new Map(order.map((id, i) => [id, i]));
    return [...list].sort((a, b) => {
        const ra = rank.get(String(a.id)) ?? Number.MAX_SAFE_INTEGER;
        const rb = rank.get(String(b.id)) ?? Number.MAX_SAFE_INTEGER;
        return ra === rb ? String(a.id).localeCompare(String(b.id)) : ra - rb;
    });
}
/**
 * Serialize an adventure to a folder of Markdown files (relative path -> content).
 * Media (images/, audio/) is unchanged and not included here.
 */
export function serializeAdventure(input) {
    const adv = normalizeAdventure(input);
    const files = {};
    const entityOrder = {};
    for (const ek of ENTITY_KINDS) {
        const list = ek.get(adv);
        if (!list.length)
            continue;
        entityOrder[ek.kind] = list.map((e) => String(e.id));
        for (const entity of list) {
            files[`${ek.dir}/${entity.id}.md`] = serializeEntity(entity, ek.kind, ek.body);
        }
    }
    // Custom-kind entities.
    for (const [kindId, list] of Object.entries(adv.entities)) {
        if (!Array.isArray(list) || !list.length)
            continue;
        const def = resolveKind(kindId, adv.kinds);
        const dir = def?.storageDir || kindId;
        const bodyField = (def && bodyFieldKey(def)) || 'body';
        entityOrder[kindId] = list.map((e) => String(e.id));
        for (const entity of list) {
            files[`${dir}/${entity.id}.md`] = serializeEntity(entity, kindId, bodyField);
        }
    }
    files[MANIFEST_FILE] = serializeManifest(adv, entityOrder);
    return files;
}
/**
 * Parse a folder of Markdown files (relative path -> content) back into an Adventure.
 * Always returns a normalized (schemaVersion 3) adventure.
 */
export function parseAdventure(files) {
    const manifestRaw = files[MANIFEST_FILE];
    if (!manifestRaw)
        throw new Error(`Missing ${MANIFEST_FILE}`);
    const { data } = parseDoc(manifestRaw);
    const manifest = data;
    const draft = {
        schemaVersion: 3,
        slug: manifest.slug,
        title: manifest.title,
        subtitle: manifest.subtitle ?? '',
        cover: manifest.cover ?? '',
        meta: manifest.meta ?? {},
        overview: manifest.overview ?? {},
        kinds: manifest.kinds ?? {},
        sections: manifest.sections ?? [],
        rooms: { floors: manifest.floors ?? [], rooms: [] },
        characters: [],
        npcs: [],
        clues: [],
        items: [],
        monsters: [],
        encounters: [],
        pages: [],
        authorNotes: manifest.authorNotes ?? '',
        entities: {},
        images: manifest.images ?? {},
    };
    if (manifest.curses !== undefined)
        draft.curses = manifest.curses;
    if (manifest.createdAt)
        draft.createdAt = manifest.createdAt;
    if (manifest.updatedAt)
        draft.updatedAt = manifest.updatedAt;
    const order = manifest.entityOrder ?? {};
    const customKinds = manifest.kinds ?? {};
    // Group entity files by their top-level directory.
    const byDir = new Map();
    for (const [path, content] of Object.entries(files)) {
        if (path === MANIFEST_FILE)
            continue;
        const slash = path.indexOf('/');
        if (slash < 0)
            continue;
        const dir = path.slice(0, slash);
        const file = path.slice(slash + 1);
        if (!file.endsWith('.md'))
            continue;
        const id = file.slice(0, -3);
        if (!byDir.has(dir))
            byDir.set(dir, []);
        byDir.get(dir).push({ id, content });
    }
    // Reverse map: custom storageDir -> kind id.
    const dirToCustomKind = new Map();
    for (const [kindId, def] of Object.entries(customKinds)) {
        const dir = def?.storageDir || kindId;
        dirToCustomKind.set(dir, kindId);
    }
    for (const [dir, entries] of byDir) {
        const builtin = ENTITY_KIND_BY_DIR.get(dir);
        if (builtin) {
            const list = entries.map((e) => parseEntity(e.content, builtin.kind, builtin.body));
            builtin.set(draft, reorderByIndex(list, order[builtin.kind]));
            continue;
        }
        // Custom kind.
        const kindId = dirToCustomKind.get(dir) ?? dir;
        const def = resolveKind(kindId, customKinds);
        const bodyField = (def && bodyFieldKey(def)) || 'body';
        const list = entries.map((e) => parseEntity(e.content, kindId, bodyField));
        draft.entities[kindId] = reorderByIndex(list, order[kindId]);
    }
    return normalizeAdventure(draft);
}
//# sourceMappingURL=markdown.js.map